import mapPoisCearaRaw from "../data/map-pois-ceara.json";
import mapPoisEscolasPublicasRaw from "../data/map-pois-escolas-publicas.json";
import mapPoisEducacaoRaw from "../data/map-pois-educacao.json";
import mapPoisSaudeRaw from "../data/map-pois-saude.json";
import mapPoisRaw from "../data/map-pois.json";
import mapRegionalWordsRaw from "../data/map-regional-words.json";
import type {
  MapPoi,
  MapPoiCategory,
  MapPoiDetails,
  MapPoiListItem,
  RawPoi,
  RegionalWord,
  RegionalWordsRegion,
} from "./map.types";

const RECENT_POIS_KEY = "bilingua:map_recent_pois";
const MAX_RECENT_POIS = 6;

type MapPoiContent = {
  pois: RawPoi[];
  mostAccessedIds?: string[];
  recentAccessedIds?: string[];
};

const MAP_CONTENT = mapPoisRaw as unknown as MapPoiContent;

const mergeUniquePois = (...groups: RawPoi[][]): RawPoi[] => {
  const byId = new Map<string, RawPoi>();
  groups.flat().forEach((poi) => byId.set(poi.id, poi));
  return Array.from(byId.values());
};

const ALL_RAW: RawPoi[] = mergeUniquePois(
  MAP_CONTENT.pois ?? [],
  (mapPoisSaudeRaw as { pois: RawPoi[] }).pois ?? [],
  (mapPoisEducacaoRaw as { pois: RawPoi[] }).pois ?? [],
  (mapPoisEscolasPublicasRaw as { pois: RawPoi[] }).pois ?? [],
  (mapPoisCearaRaw as { pois: RawPoi[] }).pois ?? []
);

const REGIONAL_REGIONS: RegionalWordsRegion[] =
  (mapRegionalWordsRaw as { regions: RegionalWordsRegion[] }).regions ?? [];

export const NEARBY_POI_RADIUS_KM = 10;

export const MAP_CATEGORIES: MapPoiCategory[] = [
  "saude",
  "educacao",
  "escola_publica",
  "comunidade",
  "acessibilidade",
];

export const CATEGORY_SEARCH_KEYWORDS: Record<MapPoiCategory, string[]> = {
  saude: [
    "hospital",
    "upa",
    "caps",
    "saude",
    "medico",
    "clinica",
    "emergencia",
  ],
  educacao: [
    "escola",
    "curso",
    "libras",
    "universidade",
    "formacao",
    "senac",
    "if",
    "ines",
    "instituto",
  ],
  escola_publica: [
    "escola",
    "estadual",
    "municipal",
    "publica",
    "bilíngue",
    "bilingue",
    "surdo",
    "rede publica",
  ],
  comunidade: ["associacao", "feneis", "cas", "comunidade", "surdo"],
  acessibilidade: [
    "acessibilidade",
    "interprete",
    "intérprete",
    "ponto",
    "libras",
  ],
};

export function normalizeMapText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getDistanceInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const earthRadiusKm = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLon / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const toMapPoi = (raw: RawPoi): MapPoi => ({
  id: raw.id,
  name: raw.title,
  category: raw.category,
  latitude: raw.latitude,
  longitude: raw.longitude,
  state: raw.state,
});

export function getMapPois(): MapPoi[] {
  return ALL_RAW.map(toMapPoi);
}

export function filterMapPois(
  search?: string,
  categories?: MapPoiCategory[]
): MapPoi[] {
  const normalized = normalizeMapText(search ?? "");
  return getMapPois().filter((poi) => {
    if (categories?.length && !categories.includes(poi.category)) return false;
    if (!normalized) return true;
    const raw = ALL_RAW.find((item) => item.id === poi.id);
    const keywords = CATEGORY_SEARCH_KEYWORDS[poi.category] ?? [];
    const haystack = normalizeMapText(
      `${poi.name} ${raw?.address ?? ""} ${raw?.subtitle ?? ""} ${
        raw?.state ?? ""
      } ${keywords.join(" ")}`
    );
    return haystack.includes(normalized);
  });
}

export function getPoiDetails(poiId: string): MapPoiDetails | null {
  const raw = ALL_RAW.find((item) => item.id === poiId);
  if (!raw) return null;
  return {
    id: raw.id,
    title: raw.title,
    category: raw.category,
    subtitle: raw.subtitle,
    address: raw.address ?? "",
    hours: raw.hours,
    phone: raw.phone,
    email: raw.email,
    website: raw.website,
    latitude: raw.latitude,
    longitude: raw.longitude,
    state: raw.state,
    services: raw.services,
  };
}

export function isPoiNearbyToUser(
  poi: { latitude?: number; longitude?: number },
  userLatitude?: number,
  userLongitude?: number
): boolean {
  if (
    poi.latitude == null ||
    poi.longitude == null ||
    userLatitude == null ||
    userLongitude == null
  ) {
    return false;
  }
  return (
    getDistanceInKm(userLatitude, userLongitude, poi.latitude, poi.longitude) <=
    NEARBY_POI_RADIUS_KM
  );
}

export function getNearbyPois(
  userLatitude: number,
  userLongitude: number,
  categories: MapPoiCategory[] = MAP_CATEGORIES
): MapPoiListItem[] {
  return ALL_RAW.filter((poi) => categories.includes(poi.category))
    .filter((poi) => isPoiNearbyToUser(poi, userLatitude, userLongitude))
    .map((poi) => ({
      id: poi.id,
      title: poi.title,
      category: poi.category,
      address: poi.address ?? "",
      hours: poi.hours,
      latitude: poi.latitude,
      longitude: poi.longitude,
      state: poi.state,
    }))
    .sort((a, b) => {
      const distA = getDistanceInKm(
        userLatitude,
        userLongitude,
        a.latitude,
        a.longitude
      );
      const distB = getDistanceInKm(
        userLatitude,
        userLongitude,
        b.latitude,
        b.longitude
      );
      return distA - distB;
    });
}

export function filterPoisNearby<
  T extends { latitude: number; longitude: number }
>(pois: T[], userLatitude?: number, userLongitude?: number): T[] {
  if (userLatitude == null || userLongitude == null) return pois;
  return pois.filter((poi) =>
    isPoiNearbyToUser(poi, userLatitude, userLongitude)
  );
}

export function getPoisByCategory(
  category: MapPoiCategory,
  userLatitude?: number,
  userLongitude?: number
): MapPoiListItem[] {
  const items = ALL_RAW.filter((poi) => poi.category === category).map(
    (poi) => ({
      id: poi.id,
      title: poi.title,
      category: poi.category,
      address: poi.address ?? "",
      hours: poi.hours,
      latitude: poi.latitude,
      longitude: poi.longitude,
      state: poi.state,
    })
  );

  if (userLatitude == null || userLongitude == null) return items;

  return [...items].sort((a, b) => {
    const distA = getDistanceInKm(
      userLatitude,
      userLongitude,
      a.latitude,
      a.longitude
    );
    const distB = getDistanceInKm(
      userLatitude,
      userLongitude,
      b.latitude,
      b.longitude
    );
    return distA - distB;
  });
}

export function getPoisByIds(ids: string[]): MapPoiListItem[] {
  return ids
    .map((id) => ALL_RAW.find((poi) => poi.id === id))
    .filter((poi): poi is RawPoi => Boolean(poi))
    .map((poi) => ({
      id: poi.id,
      title: poi.title,
      category: poi.category,
      address: poi.address ?? "",
      hours: poi.hours,
      latitude: poi.latitude,
      longitude: poi.longitude,
      state: poi.state,
    }));
}

export function getPoisMostAccessed(): MapPoiListItem[] {
  const ids = MAP_CONTENT.mostAccessedIds ?? [];
  return getPoisByIds(ids);
}

export function getPoisRecentAccessed(): MapPoiListItem[] {
  try {
    const stored = localStorage.getItem(RECENT_POIS_KEY);
    if (stored) {
      const ids = JSON.parse(stored) as string[];
      if (ids.length) return getPoisByIds(ids);
    }
  } catch {
    // fallback
  }
  return getPoisByIds(MAP_CONTENT.recentAccessedIds ?? []);
}

export function recordPoiAccess(poiId: string): void {
  try {
    const stored = localStorage.getItem(RECENT_POIS_KEY);
    const current = stored ? (JSON.parse(stored) as string[]) : [];
    const next = [poiId, ...current.filter((id) => id !== poiId)].slice(
      0,
      MAX_RECENT_POIS
    );
    localStorage.setItem(RECENT_POIS_KEY, JSON.stringify(next));
  } catch {
    // ignorar
  }
}

export function getRegionalWordsByState(
  stateCode?: string
): RegionalWordsRegion | null {
  if (!stateCode) return null;
  const code = stateCode.trim().toUpperCase();
  return REGIONAL_REGIONS.find((region) => region.state === code) ?? null;
}

export function getRegionalWordsNearCoords(
  latitude?: number,
  longitude?: number
): RegionalWordsRegion | null {
  if (latitude == null || longitude == null) return null;
  let nearest: RegionalWordsRegion | null = null;
  let minDist = Number.POSITIVE_INFINITY;
  REGIONAL_REGIONS.forEach((region) => {
    const dist = getDistanceInKm(
      latitude,
      longitude,
      region.latitude,
      region.longitude
    );
    if (dist < minDist) {
      minDist = dist;
      nearest = region;
    }
  });
  return nearest;
}

export function getRegionalWordsForUser(
  userState?: string,
  userLatitude?: number,
  userLongitude?: number
): RegionalWordsRegion | null {
  return (
    getRegionalWordsByState(userState) ??
    getRegionalWordsNearCoords(userLatitude, userLongitude)
  );
}

export function getAllRegionalWords(): RegionalWordsRegion[] {
  return REGIONAL_REGIONS;
}

export function getRegionalWordList(
  region: RegionalWordsRegion | null
): RegionalWord[] {
  return region?.words ?? [];
}
